import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { FileSignature, Plus, X, Eye, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Leases() {
  const { user } = useAuth();
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const reload = async () => {
    const [l, t, u] = await Promise.all([
      api.get(`/leases?sort_by=${sortBy}&sort_dir=${sortDir}`),
      api.get("/tenants").catch(()=>({data:[]})),
      api.get("/units").catch(()=>({data:[]}))
    ]);
    setLeases(l.data); setTenants(t.data); setUnits(u.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, [sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  };

  const SortHead = ({ k, children }) => {
    const Icon = sortBy === k ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th onClick={() => toggleSort(k)} className="cursor-pointer select-none hover:text-[#C9A227] transition-colors" data-testid={`sort-${k}`}>
        <span className="inline-flex items-center gap-1">{children}<Icon size={12} className="opacity-60"/></span>
      </th>
    );
  };

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      tenant_id: f.get("tenant_id"),
      unit_id: f.get("unit_id"),
      lease_start_date: f.get("start_date"),
      term_months: Number(f.get("term") || 12),
      use_of_apartment: f.get("use") || "",
      witness_name: f.get("witness_name") || null,
      witness_address: f.get("witness_address") || null,
      witness_occupation: f.get("witness_occupation") || null,
    };
    // Optional financial overrides — only sent if user filled them in.
    ["rent_amount","caution_fee","agreement_fee","service_charge","commission_rate","commission_amount"].forEach(k => {
      const v = f.get(k);
      if (v !== null && v !== "") payload[k] = Number(v);
    });
    try {
      await api.post("/leases/generate", payload);
      toast.success("Lease generated. Use Send to deliver to tenant.");
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const filtered = filter === "all" ? leases : leases.filter(l => l.lease_status === filter);
  const canCreate = ["admin","manager"].includes(user.role);
  const canBulk = ["admin","manager","lawyer"].includes(user.role);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(l => l.id)));
  };
  const bulkDownload = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post("/leases/bulk-download", { lease_ids: [...selected] }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modav-leases-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${selected.size} lease${selected.size > 1 ? "s" : ""} downloaded as ZIP`);
      setSelected(new Set());
    } catch (err) { toast.error(formatApiError(err) || "Bulk download failed"); }
    finally { setBulkLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Leases</h1>
          <p className="text-[#5E7166] text-sm">{leases.length} agreements on file.</p>
        </div>
        {canCreate && <button onClick={() => setCreating(true)} className="btn-gold" data-testid="generate-lease-btn"><Plus size={16}/> Generate Lease</button>}
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all","draft","sent_to_tenant","tenant_signed","manager_signed","lawyer_signed","completed","renewed","cancelled"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={filter===s ? "btn-emerald text-xs" : "btn-outline-emerald text-xs"} data-testid={`lease-filter-${s}`}>{statusLabel(s)}</button>
        ))}
      </div>

      <Card className="!p-0 overflow-hidden">
        {canBulk && selected.size > 0 && (
          <div className="px-4 py-3 bg-[#FDF6E3]/60 border-b border-[#E5EBE8] flex items-center justify-between text-sm" data-testid="bulk-toolbar">
            <div className="font-semibold text-[#1A2E24]">
              {selected.size} selected
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="btn-outline-emerald text-xs" data-testid="bulk-clear-btn">Clear</button>
              <button onClick={bulkDownload} disabled={bulkLoading} className="btn-gold text-xs inline-flex items-center gap-1.5" data-testid="bulk-download-btn">
                <Download size={14}/> {bulkLoading ? "Preparing…" : `Download ${selected.size} as ZIP`}
              </button>
            </div>
          </div>
        )}
        {filtered.length === 0 ? <div className="p-8 text-center text-[#5E7166]">No leases yet.</div> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr>
                {canBulk && (
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll}
                      data-testid="bulk-select-all"
                      title="Select all visible"
                    />
                  </th>
                )}
                <th>Lease</th><SortHead k="tenant_name">Tenant</SortHead><th>Unit</th><SortHead k="period">Period</SortHead><th>Total</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.map(l => (
                  <tr key={l.id} data-testid={`lease-row-${l.id}`}>
                    {canBulk && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(l.id)}
                          onChange={() => toggleSelect(l.id)}
                          data-testid={`bulk-select-${l.id}`}
                        />
                      </td>
                    )}
                    <td><div className="font-semibold font-mono text-xs">{l.id.slice(0,8)}</div></td>
                    <td>{l.tenant?.full_name || "—"}</td>
                    <td><div>{l.unit?.unit_name || "—"}</div><div className="text-xs text-[#5E7166]">{l.unit_type_name}</div></td>
                    <td className="text-xs">{formatDate(l.lease_start_date)} →<br/>{formatDate(l.lease_end_date)}</td>
                    <td className="font-serif font-bold text-[#0E5C3A]">{naira(l.total_payable)}</td>
                    <td><span className={statusBadgeClass(l.lease_status)}>{statusLabel(l.lease_status)}</span></td>
                    <td><Link to={`/leases/${l.id}`} className="btn-outline-emerald text-xs"><Eye size={12}/> Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="generate-lease-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Generate Lease</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenant</span>
                <select name="tenant_id" required className="input-modav mt-1" data-testid="lease-tenant-select">
                  <option value="">— Select tenant —</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name} ({t.email})</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit</span>
                <select name="unit_id" required className="input-modav mt-1" data-testid="lease-unit-select">
                  <option value="">— Select unit —</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.unit_name} — {u.unit_type_name} ({u.status})</option>)}
                </select></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Start Date</span><input type="date" name="start_date" required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Term (months)</span><input type="number" name="term" defaultValue={12} className="input-modav mt-1"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Use of Apartment</span><input name="use" placeholder="Residential / Tailoring / Pharmacy etc." className="input-modav mt-1"/></label>
              <div className="border-t border-[#E5EBE8] pt-3">
                <details>
                  <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-[#5E7166] hover:text-[#0E5C3A] select-none" data-testid="lease-financial-override-toggle">
                    Custom financial terms (optional) ▾
                  </summary>
                  <div className="mt-3 space-y-2 bg-[#FDF6E3]/30 p-3 rounded-lg">
                    <div className="text-[10px] text-[#A0801C] italic mb-1">Leave blank to use the standard pricing for this unit type. Fill any field to override just that one.</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" min="0" step="500" name="rent_amount" placeholder="Rent (₦)" className="input-modav text-sm" data-testid="lease-override-rent"/>
                      <input type="number" min="0" step="500" name="caution_fee" placeholder="Caution Fee (₦)" className="input-modav text-sm" data-testid="lease-override-caution"/>
                      <input type="number" min="0" step="500" name="agreement_fee" placeholder="Agreement Fee (₦)" className="input-modav text-sm" data-testid="lease-override-agreement"/>
                      <input type="number" min="0" step="500" name="service_charge" placeholder="Service Charge (₦)" className="input-modav text-sm" data-testid="lease-override-service"/>
                      <input type="number" min="0" max="100" step="0.1" name="commission_rate" placeholder="Commission %" className="input-modav text-sm" data-testid="lease-override-rate"/>
                      <input type="number" min="0" step="500" name="commission_amount" placeholder="Commission (₦)" className="input-modav text-sm" data-testid="lease-override-commission"/>
                    </div>
                    <div className="text-[10px] text-[#5E7166] mt-1">Commission auto-derives from (Rent + Caution) × Rate% unless you pin a specific amount.</div>
                  </div>
                </details>
              </div>
              <div className="border-t border-[#E5EBE8] pt-3">
                <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166] mb-2">Witness (optional)</div>
                <input name="witness_name" placeholder="Witness name" className="input-modav mb-2"/>
                <input name="witness_address" placeholder="Witness address" className="input-modav mb-2"/>
                <input name="witness_occupation" placeholder="Witness occupation" className="input-modav"/>
              </div>
              <div className="bg-[#FDF6E3]/40 text-xs p-3 rounded-lg text-[#A0801C]">Standard pricing is pulled from the active Pricing table by default. You can still edit financial terms later while the lease is in draft status.</div>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="lease-submit-btn">Generate</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
